import {
    type BoundingBox,
    Vec3
} from 'playcanvas';

import { createFigure8Track } from './animation/create-figure8-track';
import { createRotateTrack } from './animation/create-rotate-track';
import { AnimController } from './cameras/anim-controller';
import { Camera, type CameraFrame, type CameraController } from './cameras/camera';
import { FlyController } from './cameras/fly-controller';
import { FlySource } from './cameras/fly-source';
import { OrbitController } from './cameras/orbit-controller';
import type { TargetSource } from './cameras/target-navigation';
import { WalkController } from './cameras/walk-controller';
import { WalkSource } from './cameras/walk-source';
import type { Collision } from './collision';
import { easeOut } from './core/math';
import { Annotation } from './settings';
import { CameraMode, Global } from './types';

const tmpCamera = new Camera();
const tmpv = new Vec3();
// 第三人称导航专用相机：用角色逻辑位姿喂给 WalkSource，
// 避免 source 误用偏后的第三人称渲染相机位置导致角色冲过目标点。
const avatarNavCamera = new Camera();

// Walk mode is only enabled when the scene's horizontal footprint is large
// enough to walk around in. Vertical extent (Y) is irrelevant — a tall but
// narrow scene isn't walkable. Both X and Z ranges must exceed this
// minimum (in metres); below it walk mode is hidden and the viewer falls
// back to fly as the default first-person mode.
const WALK_MIN_HORIZONTAL_RANGE = 5;

const isWalkAllowed = (bbox: BoundingBox, collision: Collision | null): boolean => {
    const { x, z } = bbox.halfExtents;
    return !!collision && x * 2 >= WALK_MIN_HORIZONTAL_RANGE && z * 2 >= WALK_MIN_HORIZONTAL_RANGE;
};

const createCamera = (position: Vec3, target: Vec3, fov: number) => {
    const result = new Camera();
    result.look(position, target);
    result.fov = fov;
    return result;
};

const createFrameCamera = (bbox: BoundingBox, fov: number) => {
    const sceneSize = bbox.halfExtents.length();
    const distance = sceneSize / Math.sin(fov / 180 * Math.PI * 0.5);
    return createCamera(
        new Vec3(2, 1, 2).normalize().mulScalar(distance).add(bbox.center),
        bbox.center,
        fov
    );
};

class CameraManager {
    update: (deltaTime: number, cameraFrame: CameraFrame) => void;

    // Re-seed the active controller from the current camera pose and
    // cancel any in-progress transition lerp. Use after externally
    // mutating `camera` and/or `state.cameraMode` to make the change
    // visible instantly.
    snap: () => void;

    // holds the camera state
    camera = new Camera();

    // exposed so optional features can read walk-mode state without owning movement logic
    walkController: WalkController;

    // independent third-person avatar controller; walk mode remains unchanged
    avatarController: WalkController;

    // holds the list of camera mode controllers
    controllers: { [key: string]: CameraController | null };

    constructor(global: Global, bbox: BoundingBox, collision: Collision | null = null) {
        const { events, settings, state, config } = global;

        const walkAllowed = isWalkAllowed(bbox, collision);

        const camera0 = settings.cameras[0]?.initial;
        const defaultFov = camera0?.fov ?? 75;
        const frameCamera = createFrameCamera(bbox, defaultFov);
        const resetCamera = camera0 ?
            createCamera(new Vec3(camera0.position), new Vec3(camera0.target), camera0.fov) :
            frameCamera;

        const getAnimTrack = (initial: Camera, isObjectExperience: boolean) => {
            const { animTracks } = settings;

            // extract the camera animation track from settings
            if (animTracks?.length > 0 && settings.startMode === 'animTrack') {
                // use the first animTrack
                return animTracks[0];
            } else if (config.skipDefaultTrack) {
                // 无显式轨迹且要求跳过默认 fallback（文件拖拽等场景）
                return null;
            } else if (isObjectExperience) {
                // create basic rotation animation if no anim track is specified
                initial.calcFocusPoint(tmpv);
                return createRotateTrack(initial.position, tmpv, initial.fov);
            }
            // non-object experience: gentle figure-8 motion from inside the scene
            initial.calcFocusPoint(tmpv);
            return createFigure8Track(initial.position, tmpv, initial.fov);

        };

        // object experience starts outside the bounding box
        const isObjectExperience = !bbox.containsPoint(resetCamera.position);
        const animTrack = getAnimTrack(resetCamera, isObjectExperience);

        const controllers = {
            orbit: new OrbitController(),
            fly: new FlyController(),
            walk: new WalkController(),
            avatar: new WalkController(),
            anim: animTrack ? new AnimController(animTrack) : null
        };
        this.controllers = controllers;

        this.walkController = controllers.walk;
        this.avatarController = controllers.avatar;

        controllers.orbit.fov = resetCamera.fov;
        controllers.fly.fov = resetCamera.fov;
        controllers.fly.collision = collision;
        controllers.walk.collision = collision;
        controllers.avatar.collision = collision;
        controllers.avatar.thirdPersonEnabled = true;
        // 角色模型实际高度约 1.03m（Blender 实测），将第三人称胶囊尺寸匹配到角色，
        // 避免碰撞胶囊比角色高一大截。仅作用于独立的 avatar 控制器，不影响 walk 模式。
        controllers.avatar.capsuleHeight = 1.03;
        controllers.avatar.capsuleRadius = 0.2;
        controllers.avatar.eyeHeight = 0.92;
        // Avatar 模式下脚底贴地：关闭胶囊悬浮，模型脚直接落在 ground 上
        controllers.avatar.hoverHeight = 0;
        // 加大 Avatar 跳跃：提高起跳初速度（≈对应跳跃高度 1m，参考 hunter-world JUMP_HEIGHT=1.0），
        // 滞空时间更长 → 走/跑跳水平距离更大。仅作用于独立 avatar 控制器，不影响 walk 模式。
        controllers.avatar.jumpSpeed = 4.6;
        // 减小空中阻尼：velocityDampingAir 是每秒保留比例，越接近 1 阻尼越小，
        // 让跳跃水平惯性保留更充分（默认 0.998 → 0.9995）。
        controllers.avatar.velocityDampingAir = 0.9995;
        // 截顶起跳水平速度：走稳态约 6.3 m/s 不受影响，奔跑高速跳被限制到此上限，
        // 让跑跳距离更克制；走跳保持不变。
        controllers.avatar.maxJumpHorizontalSpeed = 7;

        const walkSource = new WalkSource();
        const avatarSource = new WalkSource();
        // Avatar 第三人称：rotate 改的是相机 yaw，不能用来转角色；
        // 改为直接给世界方向的 move，由 WalkController 根据移动方向平滑转角色 yaw。
        avatarSource.avatarMode = true;
        const flySource = new FlySource();
        const sourcesByMode: Partial<Record<CameraMode, TargetSource>> = {
            walk: walkSource,
            avatar: avatarSource,
            fly: flySource
        };
        walkSource.onComplete = avatarSource.onComplete = flySource.onComplete = () => {
            events.fire('navigateComplete');
        };

        const getController = (cameraMode: CameraMode): CameraController => {
            return controllers[cameraMode] as CameraController;
        };

        // set the global animation flag
        state.hasAnimation = !!controllers.anim;
        state.animationDuration = controllers.anim ? controllers.anim.animState.cursor.duration : 0;

        // initialize camera mode and initial camera position
        state.cameraMode = state.hasAnimation ? 'anim' : (isObjectExperience ? 'orbit' : (walkAllowed ? 'walk' : 'fly'));
        this.camera.copy(resetCamera);

        const target = new Camera(this.camera);             // the active controller updates this
        const from = new Camera(this.camera);               // stores the previous camera state during transition
        const defaultMode: CameraMode = isObjectExperience ? 'orbit' : (walkAllowed ? 'walk' : 'fly');
        let fromMode: CameraMode = defaultMode;

        // tracks the mode to restore when exiting walk
        let preWalkMode: CameraMode = isObjectExperience ? 'orbit' : 'fly';

        // enter the initial controller
        getController(state.cameraMode).onEnter(this.camera);

        // transition state
        const transitionSpeed = 1.0;
        let transitionTimer = 1;
        let clearOrbitTargetOnTransitionEnd = false;

        // start a new camera transition from the current pose
        const startTransition = () => {
            from.copy(this.camera);
            transitionTimer = 0;
        };

        this.snap = () => {
            getController(state.cameraMode).onEnter(this.camera);
            target.copy(this.camera);
            transitionTimer = 1;
            global.app.renderNextFrame = true;
        };

        // application update
        this.update = (deltaTime: number, frame: CameraFrame) => {

            // use dt of 0 if animation is paused
            const dt = state.cameraMode === 'anim' && state.animationPaused ? 0 : deltaTime;

            // update transition timer
            const prevTransitionTimer = transitionTimer;
            transitionTimer = Math.min(1, transitionTimer + deltaTime * transitionSpeed);

            const controller = getController(state.cameraMode);

            // 第三人称 avatar 模式下，渲染相机在角色后方，需改用角色逻辑位姿做导航判定
            let navCamera = this.camera;
            if (state.cameraMode === 'avatar') {
                avatarNavCamera.position.copy(controllers.avatar.navPosition);
                // 位置用角色逻辑位姿，但 yaw 必须用渲染相机 yaw：
                // WalkController._step 把 move[] 投影到相机 yaw 基底解释，
                // WalkSource 在 avatarMode 下也按相机 yaw 基底输出 move。
                avatarNavCamera.angles.set(0, this.camera.angles.y, 0);
                navCamera = avatarNavCamera;
            }
            sourcesByMode[state.cameraMode]?.update(dt, navCamera, frame);

            controller.update(dt, frame, target);

            if (transitionTimer < 1) {
                // lerp away from previous camera during transition
                this.camera.lerp(from, target, easeOut(transitionTimer));
            } else {
                this.camera.copy(target);
            }

            // update animation timeline
            if (state.cameraMode === 'anim') {
                state.animationTime = controllers.anim.animState.cursor.value;
            }

            if (clearOrbitTargetOnTransitionEnd && prevTransitionTimer < 1 && transitionTimer === 1) {
                clearOrbitTargetOnTransitionEnd = false;
                events.fire('orbitTarget:clear');
            }
        };

        // handle input events
        events.on('inputEvent', (eventName) => {
            switch (eventName) {
                case 'frame':
                    events.fire('orbitTarget:clear');
                    state.cameraMode = 'orbit';
                    controllers.orbit.goto(frameCamera);
                    startTransition();
                    break;
                case 'reset':
                    if (state.cameraMode === 'walk' || state.cameraMode === 'avatar') {
                        sourcesByMode[state.cameraMode]?.cancel();
                        events.fire('navTarget:clear');
                        startTransition();
                        (getController(state.cameraMode) as WalkController).resetToSpawn(target);
                    } else if (state.cameraMode === 'fly') {
                        flySource.cancel();
                        startTransition();
                        controllers.fly.resetToSpawn(target);
                    } else {
                        events.fire('orbitTarget:clear');
                        state.cameraMode = 'orbit';
                        controllers.orbit.goto(resetCamera);
                        startTransition();
                    }
                    break;
                case 'playPause':
                    if (state.hasAnimation) {
                        if (state.cameraMode === 'anim') {
                            // 暂停运镜：切到 orbit（cameraMode:changed 里把支点收到相机），
                            // 让用户暂停后能以相机为中心原地环视，而不是停在冻结的 anim 帧无法操作。
                            // anim cursor 不推进，再次播放时从当前时间继续。
                            state.cameraMode = 'orbit';
                        } else {
                            // 播放/恢复运镜：回到 anim 从当前时间继续
                            state.cameraMode = 'anim';
                            state.animationPaused = false;
                        }
                    }
                    break;
                case 'requestFirstPerson':
                    state.cameraMode = 'fly';
                    break;
                case 'toggleWalk':
                    if (walkAllowed) {
                        if (state.cameraMode === 'walk') {
                            state.cameraMode = preWalkMode;
                        } else {
                            preWalkMode = state.cameraMode;
                            state.cameraMode = 'walk';
                        }
                    }
                    break;
                case 'toggleAvatar':
                    if (walkAllowed) {
                        if (state.cameraMode === 'avatar') {
                            state.cameraMode = preWalkMode;
                        } else {
                            preWalkMode = state.cameraMode;
                            state.cameraMode = 'avatar';
                        }
                    }
                    break;
                case 'exitWalk':
                    if (state.cameraMode === 'walk' || state.cameraMode === 'avatar') {
                        state.cameraMode = preWalkMode;
                    }
                    break;
                case 'cancel':
                    if (state.cameraMode === 'anim') {
                        state.cameraMode = fromMode;
                    }
                    break;
                case 'interrupt':
                    if (state.cameraMode === 'anim') {
                        state.cameraMode = fromMode;
                    }
                    break;
            }
        });

        // handle camera mode switching
        events.on('cameraMode:changed', (value: CameraMode, prev: CameraMode) => {
            sourcesByMode[prev]?.cancel();

            // snapshot the current pose before any controller mutation
            startTransition();

            target.copy(this.camera);
            fromMode = prev;

            // exit the old controller
            const prevController = getController(prev);
            prevController.onExit(this.camera);

            // enter new controller
            const newController = getController(value);
            // walk ↔ avatar 切换：位置锚定到前一个控制器的角色眼睛位置
            // (navPosition)，避免第三人称下渲染相机偏后导致角色被"传送"。
            // 角度沿用当前渲染相机角度，不绕到角色朝向方向；进入 walk 第一人称后
            // 角色朝向自然跟随相机 yaw。
            if ((prev === 'walk' || prev === 'avatar') && (value === 'walk' || value === 'avatar')) {
                const prevWalk = prev === 'avatar' ? controllers.avatar : controllers.walk;
                tmpCamera.copy(this.camera);
                tmpCamera.position.copy(prevWalk.navPosition);
                newController.onEnter(tmpCamera);
            } else if (prev === 'anim' && value === 'orbit') {
                // 运镜暂停/中断切回 orbit：track 的 look target 通常离相机很远，
                // 沿用该 distance 会让 orbit 绕远点旋转，相机在大半径上很容易甩出场景。
                // 这里把支点收到相机自身（distance≈0），改为以相机为中心做原地环视，
                // 避免绕远 target 旋转导致超出场景。
                tmpCamera.copy(this.camera);
                tmpCamera.distance = 0;
                newController.onEnter(tmpCamera);
                // orbit 支点已锚定到当前相机位姿，target≈from，过渡 lerp 没有意义；
                // 拖拽中断时切换与拖拽是同一个 pointer，若仍走 1s easeOut 过渡，
                // 第一次拖拽会被过渡插值拖住产生镜头延迟感。直接 snap 让首拖即 1:1。
                transitionTimer = 1;
            } else {
                newController.onEnter(this.camera);
            }
        });

        // set animation playback speed (exposed by headless viewer)
        events.on('setAnimSpeed', (speed: number) => {
            if (controllers.anim) {
                controllers.anim.animState.cursor.timeScale = speed;
            }
        });

        // handle user scrubbing the animation timeline
        events.on('scrubAnim', (time) => {
            // switch to animation camera if we're not already there
            state.cameraMode = 'anim';

            // set time
            controllers.anim.animState.cursor.value = time;
            // 重新求值样条曲线，让 snap() 的 onEnter 读取到正确位姿
            controllers.anim.animState.update(0);
            // 取消过渡 lerp，让拖拽指针时相机直接跳到目标位置
            this.snap();
        });

        // handle user picking in the scene
        events.on('pick', (position: Vec3) => {
            // switch to orbit camera on pick
            state.cameraMode = 'orbit';

            // construct camera
            tmpCamera.copy(this.camera);
            tmpCamera.look(this.camera.position, position);

            controllers.orbit.goto(tmpCamera);
            startTransition();
            clearOrbitTargetOnTransitionEnd = true;
        });

        events.on('annotation.activate', (annotation: Annotation) => {
            events.fire('orbitTarget:clear');

            // switch to orbit camera on pick
            state.cameraMode = 'orbit';

            const { initial } = annotation.camera;

            // construct camera
            tmpCamera.fov = initial.fov;
            tmpCamera.look(
                new Vec3(initial.position),
                new Vec3(initial.target)
            );

            controllers.orbit.goto(tmpCamera);
            startTransition();
        });

        // tap-to-navigate: start auto-driving the active mode toward a picked position
        events.on('navigateTo', (position: Vec3, normal: Vec3, speedMul = 1) => {
            const source = sourcesByMode[state.cameraMode];
            if (source) {
                source.navigateTo(position, speedMul);
                events.fire('navTarget:set', position, normal);
            }
        });

        // cancel any active auto-navigation in the current mode
        events.on('navigateCancel', () => {
            sourcesByMode[state.cameraMode]?.cancel();
            events.fire('navTarget:clear');
        });

        events.on('navigateComplete', () => {
            events.fire('navTarget:clear');
        });
    }
}

export { CameraManager, isWalkAllowed };
